import { forwardRef, useEffect, useMemo } from 'react'
import { getAspectRatio } from 'src/helpers'
import { useImage } from 'src/hooks/useImage'
import { HiResImage, Image, ImageWrapper } from './ImageMagazine.style'

export interface ImageMagazineProps {
  image: string
  description: string
  hiResImage?: string
  enableHiRes?: boolean
  onLoad?: () => void
  onHiResLoad?: () => void
}

/**
 * Custom image element with support for high resolution images.
 * Created with Cloudnary Image Hosting in mind.
 * @see {@link SimpleImageMagazine} for a more simple and straight forward implementation, without high res support.
 */
export const ImageMagazine = forwardRef<HTMLDivElement, ImageMagazineProps>((props, ref) => {
  const { image, description, enableHiRes, hiResImage = image, onLoad, onHiResLoad } = props

  const { data: imageDimensions, isFetched } = useImage(image)

  const { isFetched: hiResIsLoaded } = useImage(hiResImage, { enabled: !!enableHiRes })

  const aspectRatio = useMemo(
    () => getAspectRatio(imageDimensions.height, imageDimensions.width),
    [imageDimensions.height, imageDimensions.width],
  )

  useEffect(() => {
    if (isFetched) {
      onLoad?.()
    }
  }, [isFetched, onLoad])

  useEffect(() => {
    if (hiResIsLoaded) {
      onHiResLoad?.()
    }
  }, [hiResIsLoaded, onHiResLoad])

  return (
    <ImageWrapper
      ref={ref}
      role="img"
      aria-label={description}
    >
      <Image
        src={image}
        css={{
          aspectRatio,
          transition: 'opacity 500ms linear',
          opacity: hiResIsLoaded && enableHiRes ? 0 : 1,
          filter: isFetched ? 'none' : 'blur(5px)',
        }}
      />
      {hiResIsLoaded && (
        <HiResImage
          src={hiResImage}
          css={{
            aspectRatio,
            opacity: enableHiRes ? 1 : 0,
          }}
        />
      )}
    </ImageWrapper>
  )
})
ImageMagazine.displayName = 'ImageMagazine'
